<main>
  <loading-overlay [isLoading]="isLoading() > 0" [fullWidth]="true"></loading-overlay>
  <p-toast position="bottom-right" key="br"></p-toast>
  <p-confirmDialog />

  <header>
    <div class="headline row-start-center gap-[1rem]">
      <h1>{{ headerTitle() }}</h1>
      @if (!isNew()) {
        <p-tag [severity]="statusSeverity()" [value]="statusLabelText()" />
      }
      @if (autoCreated()) {
        <p-tag severity="info" [value]="'ApPayment_AutoCreated' | translateWF: 'Auto-created'" />
      }
      @if (requestId()) {
        <a class="text-primary cursor-pointer text-sm row-start-center gap-1" (click)="openRequest()">
          <i class="pi pi-arrow-up-right"></i> {{ 'ApPayment_FromRequest' | translateWF: 'From request' }}
        </a>
      }
    </div>
    <div class="buttons">
      <!-- #5223 — a draft has posted nothing, so it can be removed outright. Hidden until the record is
           saved (there is nothing to delete before that) and again once it leaves Draft. The API applies
           the same rule; this only keeps the button off screen when it would be refused. -->
      @if (isDraft() && currentId()) {
        <p-button [label]="'Delete' | translateWF: 'Delete'" icon="pi pi-trash" severity="danger" variant="outlined" (click)="confirmDelete()" />
      }

      <span class="separator"></span>
      @if (isDraft() && showPaymentForm()) {
        <p-button [label]="'SaveDraft' | translateWF: 'Save draft'" icon="pi pi-save" variant="outlined" (click)="saveDraft()" />
      }
      @if (isDraft() && showPaymentForm()) {
        <p-button [label]="'Post' | translateWF: 'Post'" icon="pi pi-check" severity="success" (click)="post()" />
      }
      <!-- A posted payment with something wrong in it. Reverses the entry, restores the invoices and the
           advances, and voids the bank movement, leaving the payment editable so it can be posted again.
           Refused by the API once the bank movement is reconciled — then the money really did move. -->
      @if (isPosted() && currentId() && canUnpost) {
        <p-button [label]="'ApPayment_Unpost' | translateWF: 'Return to draft'" icon="pi pi-undo" severity="danger" variant="outlined" (click)="confirmUnpost()" />
      }
      <p-button [label]="'Cancel' | translateWF: 'Cancel'" variant="outlined" icon="pi pi-arrow-left" (click)="back()" />
    </div>
  </header>

  <div class="content">
    @if (showRequestPicker()) {
      <!-- Payment Request path: choose an approved request; selecting it opens (or spawns) its draft payment. -->
      <p-panel [header]="'ApPayment_Section_RequestPicker' | translateWF: 'Pay an approved request'">
        <p-card>
          <p class="text-sm text-surface-500 mb-3">
            {{
              'ApPayment_RequestPicker_Hint'
                | translateWF
                  : 'Choose an approved payment request to open its draft
          payment, then settle it from a bank account.'
            }}
          </p>
          <div class="form-box max-w-[40rem]">
            <label for="request">{{ 'ApPayment_Request' | translateWF: 'Approved request' }}</label>
            <p-select
              id="request"
              [formControl]="selectedRequestControl"
              [options]="requestOptions()"
              optionLabel="label"
              optionValue="value"
              [filter]="true"
              [showClear]="true"
              appendTo="body"
              styleClass="w-full"
              [placeholder]="'ApPayment_Request_Placeholder' | translateWF: 'Select an approved request'"
              (onChange)="onRequestSelected($event.value)"
            />
            @if (requestOptions().length === 0) {
              <small class="text-surface-500">{{
                'ApPayment_NoApprovedRequests'
                  | translateWF
                    : 'No approved requests are
            awaiting payment.'
              }}</small>
            }
          </div>
        </p-card>
      </p-panel>
    } @else {
      <form [formGroup]="form" class="grid-12 gap-[1.5rem]">
        <!-- LEFT COLUMN -->
        <div class="span-12-12-8 col">
          <!-- Payment -->
          <p-panel [header]="'ApPayment_Section_Payment' | translateWF: 'Payment'" [toggleable]="true">
            <p-card>
              <div class="grid-12">
                <div class="span-12">
                  <div class="form-box">
                    <label for="provider">{{ 'ApPayment_Provider' | translateWF: 'Provider' }} <span class="text-red-500">*</span></label>
                    <p-autoComplete
                      id="provider"
                      formControlName="provider"
                      #providerSearch="cslServerSearch"
                      [cslServerSearch]="fetchProviders"
                      [suggestions]="providerSearch.results()"
                      (onSelect)="onProviderSelected($event.value)"
                      (onClear)="onProviderCleared()"
                      optionLabel="name"
                      [forceSelection]="true"
                      [dropdown]="true"
                      dropdownIcon="pi pi-search"
                      [minLength]="1"
                      appendTo="body"
                      styleClass="w-full"
                      inputStyleClass="w-full"
                      [ngClass]="{ 'ng-invalid ng-dirty': form.controls.provider.invalid && form.controls.provider.touched }"
                    >
                      <ng-template let-business pTemplate="item">
                        <span
                          ><strong>{{ business.code }}</strong> — {{ business.name }}</span
                        >
                      </ng-template>
                    </p-autoComplete>
                    @if (fromRequest()) {
                      <small class="text-surface-500">
                        <i class="pi pi-lock text-xs"></i>
                        {{
                          'ApPayment_Provider_FromRequest'
                            | translateWF
                              : 'Provider
                    comes from the payment request and cannot be changed.'
                        }}
                      </small>
                    } @else {
                      <small class="text-surface-500">{{
                        'ApPayment_Provider_Hint'
                          | translateWF
                            : 'Selecting a provider
                    loads its open invoices below.'
                      }}</small>
                    }
                  </div>
                </div>

                <div class="span-12-6">
                  <div class="form-box">
                    <label for="currency">{{ 'ApPayment_Currency' | translateWF: 'Currency' }}</label>
                    <input id="currency" type="text" pInputText class="w-full" [value]="currencyLabel(form.controls.currency.value)" readonly />
                    <small class="text-surface-500">{{
                      'ApPayment_Currency_FromBank'
                        | translateWF
                          : 'From the source bank account — the money leaves it, so
                    the payment is in its currency.'
                    }}</small>
                  </div>
                </div>
                <div class="span-12-6">
                  <div class="form-box">
                    <label for="paymentDate">{{ 'ApPayment_Date' | translateWF: 'Payment date' }} <span class="text-red-500">*</span></label>
                    <p-datepicker
                      id="paymentDate"
                      formControlName="paymentDate"
                      [showIcon]="true"
                      appendTo="body"
                      styleClass="w-full"
                      [ngClass]="{ 'ng-invalid ng-dirty': form.controls.paymentDate.invalid && form.controls.paymentDate.touched }"
                    />
                  </div>
                </div>
              </div>
            </p-card>
          </p-panel>

          <!-- Nothing is transferred when the advances cover the whole settlement (#4349): the entry is
               Dr AP / Cr Anticipo. Said above the bank section because that is the section it excuses. -->
          <app-received-coverage-banner [coverages]="selectedCoverage()" />
          @if (fullyCoveredByAdvance()) {
            <p-message severity="info" styleClass="w-full">
              {{
                'ApPayment_FullyCoveredByAdvance'
                  | translateWF
                    : 'These invoices are fully covered by a vendor advance, so no money is transferred. Posting settles them
          against the advance — no bank account, fees or transfer reference are needed.'
              }}
            </p-message>
          }

          <!-- Bank, reference, charges & documents (shared component) -->
          <!-- Everything bank-side falls away when the advance covers the settlement: no account funds it,
               no wire carries a reference, no transfer is charged a fee and nothing reaches a destination.
               What is left is the documents, which is what the title then says. -->
          <app-bank-charges-section
            [title]="
              fullyCoveredByAdvance()
                ? ('ApPayment_Section_Documents' | translateWF: 'Documents')
                : ('ApPayment_Section_Bank' | translateWF: 'Account, reference, charges & documents')
            "
            [bankAccountControl]="fullyCoveredByAdvance() ? null : form.controls.bankAccount"
            [referenceControl]="fullyCoveredByAdvance() ? null : form.controls.bankTransactionReference"
            [bankingFeeControl]="fullyCoveredByAdvance() ? undefined : form.controls.bankingFee"
            [securityFeeControl]="fullyCoveredByAdvance() ? undefined : form.controls.securityFee"
            [bankAccountOptions]="bankAccountOptions()"
            [destinationControl]="fullyCoveredByAdvance() ? undefined : form.controls.selectedProviderBankId"
            [destinationOptions]="providerBankOptions()"
            [destinationPlaceholder]="'ApPayment_SelectDestination' | translateWF: 'Select a provider bank account'"
            [noDestinationHint]="'ApPayment_NoDestination_Hint' | translateWF: 'This provider has no bank account in this currency — add one on the provider record.'"
            [destinationHint]="
              destinationLockedToRequest() ? ('ApPayment_Destination_FromRequest' | translateWF: 'Set by the approved payment request — it cannot be changed here.') : ''
            "
            [currencyLabel]="currencyLabel(currency())"
            [attachments]="attachments()"
            [canAttach]="isPersisted()"
            [bankingFeeHint]="'ApPayment_BankingFee_Hint' | translateWF: 'Wire / transfer charge — added to the total paid.'"
            [securityFeeHint]="'ApPayment_SecurityFee_Hint' | translateWF: 'Insurance / handling — added to the total paid.'"
            (fileSelected)="onReceiptFile($event)"
            (attachmentOpened)="openAttachment($event)"
          />

          <!-- Allocate to open invoices -->
          <p-panel [toggleable]="true">
            <ng-template pTemplate="header">
              <div class="row-between-center w-full">
                <span class="font-semibold">{{ 'ApPayment_Section_Allocate' | translateWF: 'Allocate to open invoices' }} <span class="text-red-500">*</span></span>
                <span class="text-sm text-surface-500">
                  {{ selectedCount() }} {{ 'ApPayment_Of' | translateWF: 'of' }} {{ totalInvoices() }} {{ 'ApPayment_InvoicesSelected' | translateWF: 'invoices selected' }}
                </span>
              </div>
            </ng-template>

            <p-card data-variant="compact">
              @if (fromRequest()) {
                <div class="row-sm-start-center mb-3 p-3 rounded bg-surface-100 text-surface-600 text-sm">
                  <i class="pi pi-info-circle"></i>
                  <span>{{
                    'ApPayment_Allocate_FromRequest'
                      | translateWF
                        : 'Invoice allocation comes from the payment
                request and cannot be edited here.'
                  }}</span>
                </div>
              }

              <p-table [value]="allocations.controls">
                <ng-template pTemplate="header">
                  <tr>
                    <th style="width: 3rem"></th>
                    <th>{{ 'ApPayment_Invoice' | translateWF: 'Invoice' }}</th>
                    <th>{{ 'ApPayment_DueDate' | translateWF: 'Due date' }}</th>
                    <th class="text-right">{{ 'ApPayment_NetPayable' | translateWF: 'Net payable' }}</th>
                    <th class="text-right" style="width: 11rem">
                      {{
                        'ApPayment_PaymentAmount'
                          | translateWF
                            : 'Amount to
                    settle'
                      }}
                    </th>
                    <th class="text-right" style="width: 11rem">
                      {{
                        'ApPayment_AdvanceToUse'
                          | translateWF
                            : 'Advance to
                    use'
                      }}
                    </th>
                    <th class="text-right">{{ 'ApPayment_CashToPay' | translateWF: 'Cash to pay' }}</th>
                    <th style="width: 14rem">{{ 'ApPayment_CashRequest' | translateWF: 'Cash request' }}</th>
                  </tr>
                </ng-template>

                <ng-template pTemplate="body" let-row>
                  @let alloc = getRowGroup(row);
                  @let docId = alloc.controls.documentReference.value;
                  @let payable = payableFor(docId);
                  @let withholdingBlocked = alloc.controls.withholdingBlocked.value;
                  <tr [formGroup]="alloc" [class.opacity-60]="withholdingBlocked">
                    <td><p-checkbox formControlName="selected" [binary]="true" [disabled]="withholdingBlocked" /></td>
                    <td>
                      <!-- Fiscal number over ours (#4788); the AP number opens the read-only quick view (#4789). -->
                      <app-document-identity
                        [documentNumber]="alloc.controls.documentNumber.value"
                        [fiscalNumber]="alloc.controls.fiscalNumber.value"
                        [linkTitle]="'ApDocument_QuickView_Open' | translateWF: 'View document detail'"
                        (documentClick)="openDocumentQuickView(docId)"
                      >
                        @if (alloc.controls.isCredit.value) {
                          <p-tag severity="warn" [value]="'ApPaymentRequest_Credit' | translateWF: 'Credit'" />
                        }
                        @if (payable?.hasAdjustments) {
                          <i class="pi pi-info-circle text-amber-500 cursor-pointer" [title]="adjustmentTooltip(docId)" (click)="toggleExpand(docId)"></i>
                        }
                        <!-- #4488/#4842 — the post rejects this document; say so here rather than hiding it. -->
                        @if (withholdingBlocked) {
                          <p-tag
                            severity="danger"
                            [title]="
                              'ApDocument_Withholding_Blocked_Hint'
                                | translateWF: 'This document needs a posted withholding certificate before it can be paid. A Financial Officer must issue one first.'
                            "
                          >
                            <i class="pi pi-lock mr-1"></i
                            >{{
                              'ApDocument_Withholding_Blocked'
                                | translateWF
                                  : 'Requires
                        Constancia de Retención'
                            }}
                          </p-tag>
                        }
                        <!-- #5662 — advice, not a lock: the row stays selectable and the post decides. -->
                        <app-received-coverage-tag [coverage]="receivedCoverage().get(docId)" />
                      </app-document-identity>
                    </td>
                    <td>{{ alloc.controls.dueDate.value ? (alloc.controls.dueDate.value | date: 'dd/MM/yyyy') : '—' }}</td>
                    <td class="text-right">{{ alloc.controls.netPayable.value | number: '1.2-2' }} {{ currencyLabel(alloc.controls.documentCurrency.value) }}</td>
                    <td class="text-right">
                      <!-- Grouped amount, distributed doc-first on submit. A credit row is NEGATIVE: netPayable is
                         already negative, so min = netPayable, max = 0.

                         This is the DISCHARGE, in the invoice's own currency (#5144) — what the invoice is
                         relieved of. The cash column derives from it. Capping here rather than on the cash
                         is deliberate: capping a foreign invoice against its functional value would make it
                         impossible to close once the Lempira strengthened. -->
                      <p-inputNumber
                        formControlName="allocatedAmount"
                        mode="decimal"
                        [minFractionDigits]="2"
                        [maxFractionDigits]="2"
                        [min]="alloc.controls.isCredit.value ? alloc.controls.netPayable.value : 0"
                        [max]="alloc.controls.isCredit.value ? 0 : alloc.controls.netPayable.value"
                        styleClass="w-full"
                      />
                      @if (isConverted(alloc)) {
                        @if (rateFor(alloc) > 0) {
                          <small class="text-surface-400 block tabular-nums">
                            {{ currencyLabel(alloc.controls.documentCurrency.value) }} &middot; &#64; {{ rateFor(alloc) | number: '1.6-10' }} &middot;
                            {{ rateSourceLabel(alloc) }}
                          </small>
                        } @else {
                          <!-- The bank's rate hasn't been entered yet, so this row's cost is genuinely unknown —
                             say so, rather than rendering the unknown as "@ 0.000000" and reading like a bug. -->
                          <small class="text-amber-600 block">
                            {{ currencyLabel(alloc.controls.documentCurrency.value) }} &middot;
                            {{ 'ApPayment_Rate_Pending' | translateWF: 'enter the bank rate above to price this row' }}
                          </small>
                        }
                      }
                    </td>
                    <td class="text-right">
                      <!-- Advance to use (#4349): auto-filled to what's available on this invoice's PO(s); lower it
                         to pay more cash. Hidden when the PO has no advance. -->
                      @if (alloc.controls.advanceAvailable.value > 0) {
                        <p-inputNumber
                          formControlName="advanceToUse"
                          mode="decimal"
                          [minFractionDigits]="2"
                          [maxFractionDigits]="2"
                          [min]="0"
                          [max]="alloc.controls.advanceAvailable.value"
                          styleClass="w-full"
                        />
                        <small class="text-surface-400 block">
                          {{ 'ApPayment_AdvanceAvail' | translateWF: 'avail.' }} {{ alloc.controls.advanceAvailable.value | number: '1.2-2' }}
                        </small>
                      } @else if (alloc.controls.advanceToUse.value > 0) {
                        <!-- Loaded/spawned payment: no live availability, but the advance was already earmarked. -->
                        <span class="tabular-nums">{{ alloc.controls.advanceToUse.value | number: '1.2-2' }} {{ currencyLabel(alloc.controls.documentCurrency.value) }}</span>
                      } @else {
                        <span class="text-surface-400">—</span>
                      }
                    </td>
                    <td class="text-right font-medium">
                      @if (isConverted(alloc) && rateFor(alloc) <= 0) {
                        <!-- No rate, no cash figure — a dash is honest
                      where 0.00 would read as "free" . -->
                        <span class="text-surface-400">—</span>
                      } @else {
                        {{ cashToPay(alloc) | number: '1.2-2' }} {{ currencyLabel(form.controls.currency.value) }}
                      }
                    </td>
                    <!-- Optional tag, closest in spirit to a cost centre: which weekly plan this money came from.
                       Never required — paying outside a cash plan is legitimate. -->
                    <td>
                      <p-select
                        [formControl]="alloc.controls.cashRequestReference"
                        [options]="cashRequestOptions()"
                        optionLabel="label"
                        optionValue="value"
                        [showClear]="true"
                        [filter]="true"
                        filterBy="label"
                        appendTo="body"
                        styleClass="w-full"
                        [placeholder]="'ApPayment_CashRequest_None' | translateWF: 'None'"
                      />
                    </td>
                  </tr>
                  @if (payable && expandedRows[docId]) {
                    <tr class="resumen-row">
                      <td></td>
                      <td colspan="7">
                        <app-payable-document-summary [payable]="payable" [currencyLabel]="currencyLabel(form.controls.currency.value)" />
                      </td>
                    </tr>
                  }
                </ng-template>

                <ng-template pTemplate="emptymessage">
                  <tr>
                    <td colspan="8">
                      {{
                        'ApPayment_NoInvoices'
                          | translateWF
                            : 'Select a provider to load its open
                    invoices.'
                      }}
                    </td>
                  </tr>
                </ng-template>
              </p-table>
              @if (allocations.length > 0 && !fromRequest()) {
                <small class="text-surface-500">{{
                  'ApPayment_Allocate_Hint'
                    | translateWF
                      : 'Amount to settle defaults to the net payable; any advance (Anticipo) on the invoice PO auto-fills and
              reduces the cash. Lower either to pay partially — it fills the document first, then its notes. Expand a
              flagged row for the breakdown.'
                }}</small>
              }
            </p-card>
          </p-panel>

          <!-- Notes -->
          <p-panel [header]="'ApPayment_Section_Notes' | translateWF: 'Notes'" [toggleable]="true" [collapsed]="true">
            <p-card>
              <div class="form-box">
                <textarea pTextarea formControlName="notes" rows="3" class="w-full" placeholder="Add a note…"></textarea>
              </div>
            </p-card>
          </p-panel>
        </div>

        <!-- RIGHT COLUMN -->
        <div class="span-12-12-4 col sticky-summary">
          <app-settlement-summary
            [title]="'ApPayment_Section_Summary' | translateWF: 'Payment summary'"
            [hint]="'ApPayment_Summary_Hint' | translateWF: 'Total cash out is the allocated amount plus banking and security fees, and must match before posting.'"
            [baseLabel]="'ApPayment_Summary_Allocated' | translateWF: 'Allocated to invoices'"
            [baseAmount]="amount()"
            [extraRows]="summaryRows()"
            [bankingFeeLabel]="'ApPayment_BankingFee' | translateWF: 'Banking fee'"
            [securityFeeLabel]="'ApPayment_SecurityFee' | translateWF: 'Security fee'"
            [bankingFee]="bankingFeeValue()"
            [securityFee]="securityFeeValue()"
            [feesReduceNet]="false"
            [netLabel]="'ApPayment_TotalCashOut' | translateWF: 'Total cash out'"
            [net]="totalCashOut()"
            [currencyLabel]="currencyLabel(currency())"
          />

          <!-- The payment's exchange rates (#5135) — the bank's wire rate (required) and/or the books translation -->
          @if (settlementRates.length > 0) {
            <p-panel [header]="'ApPayment_Section_ExchangeRate' | translateWF: 'Exchange rate'" [toggleable]="true">
              <div formArrayName="settlementRates" class="grid grid-cols-12 gap-4">
                @for (quote of settlementRates.controls; track quote.controls.currency.value; let i = $index) {
                  <div class="col-span-12 md:col-span-6" [formGroupName]="i">
                    <div class="form-box">
                      <label [for]="'rate-' + i">
                        {{ 'ApPayment_Rate' | translateWF: 'Rate' }} &middot; {{ currencyLabel(quote.controls.currency.value) }}
                        {{ 'ApPayment_Rate_PerUsd' | translateWF: 'per USD' }}
                        @if (isBankQuote(quote)) {
                          <span class="text-red-500">*</span>
                        }
                      </label>
                      <p-inputNumber
                        [inputId]="'rate-' + i"
                        formControlName="rateToUsd"
                        mode="decimal"
                        [minFractionDigits]="2"
                        [maxFractionDigits]="rateDecimals"
                        [min]="0"
                        [readonly]="!isDraft()"
                        styleClass="w-full"
                        [placeholder]="isBankQuote(quote) ? ('ApPayment_Rate_BankPlaceholder' | translateWF: 'Rate from the bank') : ''"
                        (onInput)="onRateInput(quote, $event.value)"
                        [ngClass]="{
                          'ng-invalid ng-dirty': rateOutOfBounds(quote) || (quote.controls.rateToUsd.invalid && quote.controls.rateToUsd.touched),
                        }"
                      />
                      @if (rateOutOfBounds(quote)) {
                        <small class="text-red-500">{{ rateOutOfBoundsMessage(quote) }}</small>
                      } @else if (isBankQuote(quote)) {
                        <small class="text-surface-500">
                          {{ rateAppliesMessage(quote) }}
                          @if (quote.controls.dayRateToUsd.value; as day) {
                            &middot; {{ 'ApPayment_Rate_TableSays' | translateWF: 'Table on payment date:' }} {{ day | number: '1.2-4' }}
                          }
                        </small>
                        @if (quote.controls.rateToUsd.invalid && quote.controls.rateToUsd.touched) {
                          <small class="text-red-500">{{
                            'ApPayment_Rate_Required'
                              | translateWF
                                : 'Enter the rate the bank gave
                  you.'
                          }}</small>
                        }
                      } @else {
                        <small class="text-surface-500">
                          {{ rateAppliesMessage(quote) }}
                          @if (rateIsOverridden(quote)) {
                            &middot; <span class="text-amber-600">{{ rateOverriddenMessage(quote) }}</span>
                            @if (isDraft()) {
                              <button type="button" class="p-0 ml-1 underline text-primary bg-transparent border-0 cursor-pointer" (click)="resetRate(quote)">
                                {{ 'ApPayment_Rate_UseToday' | translateWF: 'Use table rate' }}
                              </button>
                            }
                          } @else {
                            &middot; {{ 'ApPayment_Rate_Today' | translateWF: 'Table rate on payment date' }}
                          }
                        </small>
                      }
                    </div>
                  </div>
                }
                <div class="col-span-12">
                  <small class="text-surface-500">{{ ratePanelHint() }}</small>
                </div>
              </div>
            </p-panel>
          }

          <!-- Accounting (#5024 #5031). No AP account row here: a payment settles several documents, each
               against its own AP control account, so there is no single one to name. -->
          @if (showAccounting()) {
            <p-panel [header]="'ApPayment_Section_Accounting' | translateWF: 'Accounting'">
              <p-card>
                <div class="summary-rows">
                  <div class="summary-rows__row row-start-center">
                    <span>{{ 'ApDocument_JournalEntry' | translateWF: 'Journal entry' }}</span>
                    <span class="ml-auto font-mono">{{ journalEntryDocumentNumber() }}</span>
                  </div>
                </div>

                <span class="block mt-3 mb-2 text-[0.7rem] font-semibold uppercase tracking-wide text-surface-500">
                  {{ 'ApDocument_GlImpact' | translateWF: 'GL impact' }}
                </span>
                <app-journal-entry-summary [references]="journalEntryReferences()" [currencyLabel]="currencyLabel(currency())" />
              </p-card>
            </p-panel>
          }
        </div>
      </form>
    }
  </div>
</main>
